import { useState } from "react";
import Navbar from "@/components/feature/Navbar";
import AuthModal from "@/components/feature/AuthModal";
import { useGame, AuctionItem } from "@/contexts/GameContext";
import { useAuth } from "@/contexts/AuthContext";
import { useTheme } from "@/contexts/ThemeContext";
import AuctionCard from "./components/AuctionCard";
import BidModal from "./components/BidModal";
import AnimBtn from "@/components/base/AnimBtn";

// ... existing code ...

type FilterTab = "all" | "mine" | "bidding";

export default function AuctionPage() {
  const { auctions } = useGame();
  const { user, openAuthModal } = useAuth();
  const { isDark } = useTheme();
  const [activeTab, setActiveTab] = useState<FilterTab>("all");
  const [sortBy, setSortBy] = useState<"endTime" | "bid" | "start">("endTime");
  const [selectedAuction, setSelectedAuction] = useState<AuctionItem | null>(null);

  const filtered = auctions.filter((a) => {
    if (activeTab === "mine") return user && a.sellerId === user.id;
    if (activeTab === "bidding") return user && a.currentBidder === user.username;
    return true;
  });

  const sorted = [...filtered].sort((a, b) => {
    if (sortBy === "bid") return b.currentBid - a.currentBid;
    if (sortBy === "start") return b.startPrice - a.startPrice;
    return new Date(a.endTime).getTime() - new Date(b.endTime).getTime();
  });

  const handleBid = (auction: AuctionItem) => {
    if (!user) { openAuthModal("login"); return; }
    setSelectedAuction(auction);
  };

  const pageBg = isDark ? "bg-[#0a0a0f]" : "bg-[#f8f8f8]";
  const textPrimary = isDark ? "text-white" : "text-gray-900";
  const textSecondary = isDark ? "text-gray-400" : "text-gray-500";
  const cardBg = isDark ? "bg-[#0f1015] border-white/8" : "bg-white border-gray-200";

  const tabs: { id: FilterTab; label: string; icon: string }[] = [
    { id: "all", label: "すべて", icon: "ri-list-check" },
    { id: "mine", label: "自分の出品", icon: "ri-store-2-line" },
    { id: "bidding", label: "入札中", icon: "ri-auction-line" },
  ];

  const totalBidVolume = auctions.reduce((s, a) => s + a.currentBid, 0);
  const activeCount = auctions.filter((a) => new Date(a.endTime) > new Date()).length;
  const myBidCount = user ? auctions.filter((a) => a.currentBidder === user.username).length : 0;

  return (
    <div className={`min-h-screen ${pageBg} transition-colors duration-300`}>
      <Navbar />
      <div className="pt-20 pb-12 px-4 md:px-6 max-w-6xl mx-auto">
        <div className="mb-8 animate-slide-up-fade">
          <h1 className={`text-3xl font-black mb-2 ${textPrimary}`}>オークション</h1>
          <p className={`text-sm ${textSecondary}`}>世界中の区画を入札で手に入れよう</p>
        </div>

        {/* Stats Bar */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-8">
          {[
            { icon: "ri-auction-line", label: "進行中", value: activeCount, color: "text-amber-400", bg: "bg-amber-400/10" },
            { icon: "ri-coin-line", label: "総入札額", value: `${totalBidVolume.toLocaleString()}`, color: "text-yellow-400", bg: "bg-yellow-400/10" },
            { icon: "ri-user-star-line", label: "入札中", value: myBidCount, color: "text-emerald-400", bg: "bg-emerald-400/10" },
            { icon: "ri-time-line", label: "総出品数", value: auctions.length, color: "text-sky-400", bg: "bg-sky-400/10" },
          ].map((s, i) => (
            <div
              key={s.label}
              className={`rounded-2xl border p-4 flex items-center gap-3 transition-all hover:-translate-y-1 cursor-default ${cardBg}`}
              style={{ animationDelay: `${i * 80}ms` }}
            >
              <div className={`w-10 h-10 flex items-center justify-center rounded-xl flex-shrink-0 ${s.bg}`}>
                <i className={`${s.icon} text-lg ${s.color}`}></i>
              </div>
              <div>
                <p className={`text-lg font-black ${s.color}`}>{s.value}</p>
                <p className={`text-xs ${textSecondary}`}>{s.label}</p>
              </div>
            </div>
          ))}
        </div>

        {/* Tabs + Sort */}
        <div className={`rounded-2xl border mb-6 overflow-hidden ${cardBg}`}>
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-0">
            <div className="flex overflow-x-auto">
              {tabs.map((tab) => (
                <AnimBtn
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  animType="pop"
                  className={`flex items-center gap-2 px-5 py-4 text-sm font-semibold transition-all cursor-pointer whitespace-nowrap flex-shrink-0 border-b-2 ${
                    activeTab === tab.id
                      ? "border-amber-400 text-amber-400"
                      : isDark
                      ? "border-transparent text-gray-400 hover:text-white"
                      : "border-transparent text-gray-500 hover:text-gray-900"
                  }`}
                >
                  <i className={`${tab.icon} text-base`}></i>
                  {tab.label}
                  <span className={`text-xs rounded-full px-1.5 py-0.5 font-bold ${
                    activeTab === tab.id
                      ? "bg-amber-400/20 text-amber-400"
                      : isDark ? "bg-white/5 text-gray-500" : "bg-gray-100 text-gray-400"
                  }`}>
                    {tab.id === "all" ? auctions.length : tab.id === "mine" ? (user ? auctions.filter(a => a.sellerId === user.id).length : 0) : myBidCount}
                  </span>
                </AnimBtn>
              ))}
            </div>
            <div className="px-4 py-3 flex-shrink-0">
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value as typeof sortBy)}
                className={`text-xs rounded-xl border px-3 py-2 cursor-pointer outline-none ${
                  isDark ? "bg-[#0f1015] border-white/10 text-gray-300" : "bg-white border-gray-200 text-gray-700"
                }`}
              >
                <option value="endTime">終了時刻順</option>
                <option value="bid">入札額順</option>
                <option value="start">開始価格順</option>
              </select>
            </div>
          </div>
        </div>

        {/* Auction Grid */}
        {sorted.length === 0 ? (
          <div className={`rounded-2xl border p-16 text-center ${cardBg}`}>
            <div className="w-16 h-16 flex items-center justify-center mx-auto mb-4 bg-amber-400/10 rounded-2xl">
              <i className="ri-auction-line text-3xl text-amber-400"></i>
            </div>
            <p className={`font-bold mb-1 ${textPrimary}`}>オークションがありません</p>
            <p className={`text-sm ${textSecondary}`}>
              {activeTab === "mine" ? "区画をオークションに出品してみよう" : activeTab === "bidding" ? "まだ入札していません" : "現在進行中のオークションはありません"}
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {sorted.map((auction) => (
              <AuctionCard key={auction.id} auction={auction} onBid={handleBid} />
            ))}
          </div>
        )}

        {/* Login prompt */}
        {!user && (
          <div className={`mt-8 rounded-2xl border p-6 flex flex-col sm:flex-row items-center justify-between gap-4 ${cardBg}`}>
            <div>
              <h3 className={`font-bold mb-1 ${textPrimary}`}>ログインして入札しよう</h3>
              <p className={`text-sm ${textSecondary}`}>アカウントを作成して世界中の区画を競り落とそう！</p>
            </div>
            <AnimBtn
              onClick={() => openAuthModal("register")}
              animType="bounce"
              className="bg-gradient-to-r from-amber-400 to-orange-500 hover:from-amber-300 hover:to-orange-400 text-black font-bold rounded-full px-8 py-3 cursor-pointer whitespace-nowrap"
            >
              無料で始める
            </AnimBtn>
          </div>
        )}
      </div>

      {selectedAuction && (
        <BidModal auction={selectedAuction} onClose={() => setSelectedAuction(null)} />
      )}
      <AuthModal />
    </div>
  );
}
